import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaFileExcel, FaChartBar, FaSyncAlt, FaCalendarAlt, FaMapMarkerAlt, FaBuilding, FaSpinner, FaExclamationCircle } from 'react-icons/fa';
import { useTheme } from '../../context/SocketContext';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, BarElement, CategoryScale, LinearScale,
  PointElement, Title, Tooltip, Legend, Filler
} from 'chart.js';
import * as XLSX from 'xlsx';

ChartJS.register(
  BarElement, CategoryScale, LinearScale,
  PointElement, Title, Tooltip, Legend, Filler
);

const AnnualReportService = () => {
  const { currentThemeColors } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [annualReportData, setAnnualReportData] = useState(null);
  const [annualReportService, setAnnualReportService] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    fetchAnnualReportData();
  }, []);

  // Helper function to format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const fetchAnnualReportData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      setIsRefreshing(true);
      setError('');

      const userResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!userResponse.ok) throw new Error('Failed to fetch user data');
      const userData = await userResponse.json();

      // Set annual report service data from user data
      if (userData.user?.annualReportService) {
        setAnnualReportService(userData.user.annualReportService);
      }

      const annualReportResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/annual-report/${userData.user._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!annualReportResponse.ok) {
        if (annualReportResponse.status === 404) {
          setAnnualReportData(null);
          return;
        }
        throw new Error('Failed to fetch annual report data');
      }

      const data = await annualReportResponse.json();
      setAnnualReportData(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const exportToExcel = () => {
    if (!annualReportData) return;
    
    const worksheet = XLSX.utils.json_to_sheet(
      annualReportData.rows.filter(row => !row.include)
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Annual Report Data");
    XLSX.writeFile(workbook, `${annualReportData.title || 'annual_report_data'}.xlsx`);
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        position: 'top',
        labels: {
          font: {
            family: 'Arial',
            size: 12,
            weight: 'bold'
          },
          padding: 20,
          usePointStyle: true,
        }
      },
      title: {
        display: true,
        text: annualReportData?.title || 'Annual Report Data',
        font: { 
          size: 18,
          family: 'Arial',
          weight: 'bold'
        },
        padding: {
          top: 10,
          bottom: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 12
        },
        padding: 10,
        usePointStyle: true,
        callbacks: {
          label: function(context) {
            return ` $${context.parsed.y.toLocaleString()}`;
          }
        }
      }
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart',
    },
    elements: {
      bar: {
        borderRadius: 6,
        borderSkipped: 'bottom',
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false
        },
        ticks: {
          font: {
            weight: 'bold'
          }
        }
      },
      y: {
        beginAtZero: false,
        grid: {
          color: 'rgba(0,0,0,0.05)',
          drawBorder: false
        },
        ticks: {
          callback: value => '$' + value.toLocaleString(),
          font: {
            weight: 'bold'
          }
        },
      },
    },
    layout: {
      padding: {
        left: 20,
        right: 20,
        top: 20,
        bottom: 20
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <FaSpinner className="w-12 h-12 text-primary-500 animate-spin mx-auto" />
          <p className="mt-4 text-gray-500 dark:text-gray-400">Loading your annual report data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 opacity-0 animate-fade-in-up" style={{ animationDelay: '0ms', animationFillMode: 'forwards' }}>
        <div>
          <h1 className="text-2xl font-bold text-dark-800 dark:text-white">
            <span style={{ color: 'var(--primary-500)' }}>Annual Report Service</span>
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">View and manage your annual report filings</p>
        </div>
        <button
          onClick={fetchAnnualReportData}
          disabled={isRefreshing}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-white hover:scale-105 disabled:opacity-50 transition-all duration-300"
          style={{ 
            background: `linear-gradient(135deg, var(--primary-500), var(--primary-600))`,
            boxShadow: `0 10px 15px -3px var(--glow-color)`
          }}
        >
          <FaSyncAlt className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <FaExclamationCircle className="w-5 h-5" />
            <span><strong>Error:</strong> {error}</span>
          </div>
          <button
            onClick={fetchAnnualReportData}
            disabled={isRefreshing}
            className="px-4 py-2 rounded-lg border border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
          >
            {isRefreshing ? 'Refreshing...' : 'Try Again'}
          </button>
        </div>
      )}

      {/* Annual Report Due Date Card */}
      {annualReportService?.dueDate && (
        <div className="card overflow-hidden opacity-0 animate-fade-in-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
          <div 
            className="p-4 text-white flex items-center justify-between"
            style={{ background: `linear-gradient(135deg, var(--accent-500), var(--primary-600))` }}
          >
            <div className="flex items-center gap-2">
              <FaCalendarAlt className="w-5 h-5" />
              <h2 className="font-semibold">Annual Report Due Date</h2>
            </div>
            <button
              onClick={fetchAnnualReportData}
              disabled={isRefreshing}
              className="p-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
            >
              <FaSyncAlt className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                  <FaMapMarkerAlt className="w-5 h-5 text-primary-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">State</p>
                  <p className="font-semibold text-dark-800 dark:text-white">{annualReportService.state}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <FaBuilding className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Entity Type</p>
                  <p className="font-semibold text-dark-800 dark:text-white">{annualReportService.entityType}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <FaCalendarAlt className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Incorporation Date</p>
                  <p className="font-semibold text-dark-800 dark:text-white">{formatDateForDisplay(annualReportService.registrationDate)}</p>
                </div>
              </div>
              <div className="p-4 rounded-xl bg-red-500 text-white text-center shadow-lg shadow-red-500/30">
                <p className="text-xs uppercase tracking-wide opacity-80 mb-1">Due Date</p>
                <p className="text-xl font-bold">{annualReportService.dueDateText || formatDateForDisplay(annualReportService.dueDate)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {annualReportData ? (
        <div className="space-y-6">
          {/* Data Table */}
          <div className="card overflow-hidden opacity-0 animate-fade-in-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
            <div 
              className="p-4 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3"
              style={{ background: `linear-gradient(135deg, var(--primary-500), var(--accent-500))` }}
            >
              <div className="flex items-center gap-2">
                <FaFileExcel className="w-5 h-5" />
                <h2 className="font-semibold">{annualReportData.additionalTitle || 'Annual Report Records'}</h2>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={exportToExcel}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm transition-colors"
                >
                  <FaFileExcel className="w-4 h-4" />
                  <span className="hidden sm:inline">Export Excel</span>
                </button>
                <button
                  onClick={fetchAnnualReportData}
                  disabled={isRefreshing}
                  className="p-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
                >
                  <FaSyncAlt className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-dark-700">
                  <tr>
                    {annualReportData.headers.map((header, index) => (
                      <th key={index} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                  {annualReportData.rows.filter(row => !row.include).map((row, rowIndex) => (
                    <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white dark:bg-dark-800' : 'bg-gray-50 dark:bg-dark-700/50'}>
                      {annualReportData.headers.map((header, colIndex) => (
                        <td key={colIndex} className="px-4 py-3 text-sm text-dark-800 dark:text-gray-300">
                          {typeof row[header] === 'number' ? `$${row[header].toLocaleString()}` : row[header]}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {!annualReportData.rows.filter(row => !row.include).length && (
                    <tr>
                      <td colSpan={annualReportData.headers.length} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                        No additional data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart Section */}
          {annualReportData.chartData && (
            <div className="card overflow-hidden opacity-0 animate-fade-in-up" style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}>
              <div 
                className="p-4 text-white flex items-center justify-between"
                style={{ background: `linear-gradient(135deg, var(--primary-500), var(--accent-500))` }}
              >
                <div className="flex items-center gap-2">
                  <FaChartBar className="w-5 h-5" />
                  <h2 className="font-semibold">{annualReportData.title || 'Annual Report Overview'}</h2>
                </div>
                <button
                  onClick={fetchAnnualReportData}
                  disabled={isRefreshing}
                  className="p-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
                >
                  <FaSyncAlt className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <div className="p-4" style={{ minHeight: '300px', height: '50vh' }}>
                <Bar data={annualReportData.chartData} options={chartOptions} />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="card p-12 text-center">
          <FaFileExcel className="w-16 h-16 text-primary-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-dark-800 dark:text-white mb-2">No annual report data available</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            Please contact your administrator to set up your annual report records
          </p>
          <button
            onClick={fetchAnnualReportData}
            disabled={isRefreshing}
            className="px-6 py-3 rounded-xl bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50 transition-colors"
          >
            {isRefreshing ? 'Checking...' : 'Check Again'}
          </button>
        </div>
      )}
    </div>
  );
};

export default AnnualReportService;
